import { useT } from '../i18n';
import type { UserMe } from '../services/api';
import type { NavView } from '../components/Layout';

interface HomeProps {
  user: UserMe | null;
  onNavigate?: (view: NavView) => void;
}

function Home({ user, onNavigate }: HomeProps) {
  const t = useT();

  // Usuario autenticado -> dashboard atual (cards de upload, extracao, revisao)
  if (user) {
    return (
      <section className="rounded-lg border border-brand-200 bg-white p-8 shadow-sm">
        <h1 className="mb-4 text-3xl font-bold text-brand-900">{t('home.title')}</h1>
        <p className="mb-6 max-w-2xl text-brand-600">{t('home.subtitle')}</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-md border border-brand-100 bg-brand-50 p-4">
            <h2 className="mb-1 font-semibold text-brand-800">{t('home.card.upload.title')}</h2>
            <p className="text-sm text-brand-600">{t('home.card.upload.desc')}</p>
          </div>
          <div className="rounded-md border border-brand-100 bg-brand-50 p-4">
            <h2 className="mb-1 font-semibold text-brand-800">{t('home.card.extraction.title')}</h2>
            <p className="text-sm text-brand-600">{t('home.card.extraction.desc')}</p>
          </div>
          <div className="rounded-md border border-brand-100 bg-brand-50 p-4">
            <h2 className="mb-1 font-semibold text-brand-800">{t('home.card.review.title')}</h2>
            <p className="text-sm text-brand-600">{t('home.card.review.desc')}</p>
          </div>
        </div>
      </section>
    );
  }

  // Visitante (nao logado) -> landing page publica
  return (
    <div className="space-y-16">
      {/* Hero */}
      <section className="rounded-lg border border-brand-200 bg-gradient-to-b from-brand-50 to-white p-8 text-center shadow-sm sm:p-12">
        <h1 className="mx-auto mb-4 max-w-3xl text-3xl font-bold text-brand-900 sm:text-4xl">
          {t('home.hero.title')}
        </h1>
        <p className="mx-auto mb-8 max-w-2xl text-brand-600 sm:text-lg">
          {t('home.hero.subtitle')}
        </p>
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => onNavigate?.('signup')}
            className="inline-block rounded-md bg-brand-600 px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            {t('home.hero.cta.signup')}
          </button>
          <button
            type="button"
            onClick={() => onNavigate?.('pricing')}
            className="inline-block rounded-md border border-brand-300 bg-white px-6 py-3 font-semibold text-brand-700 transition-colors hover:bg-brand-50"
          >
            {t('home.hero.cta.pricing')}
          </button>
        </div>
      </section>

      {/* Issue #594: Como funciona — 4 passos do fluxo real do produto
          (e-mail dedicado captura-*@ ou upload manual -> extracao
          automatica -> revisao -> exportacao). */}
      <section aria-labelledby="home-how">
        <h2 id="home-how" className="mb-8 text-center text-2xl font-bold text-brand-900 sm:text-3xl">
          {t('home.howItWorks.title')}
        </h2>
        <ol className="grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((n) => (
            <li key={n} className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
              <div
                aria-hidden="true"
                className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-base font-bold text-brand-700"
              >
                {n}
              </div>
              <h3 className="mb-2 font-semibold text-brand-800">
                {t(`home.howItWorks.step${n}.title`)}
              </h3>
              <p className="text-sm text-brand-600">{t(`home.howItWorks.step${n}.desc`)}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Issue #594: O que capturamos — APENAS doc types reais do pipeline
          (backend/app/core/pipeline): NFS-e, NFCom/NFT mod. 62 (templates
          Algar/Claro/Oi/Sercomtel/TIM/Vivo + generico), NF-e/NFC-e via XML
          e recibos/PDFs/imagens via OCR/VLM; containers ZIP e e-mail. */}
      <section aria-labelledby="home-captures">
        <h2 id="home-captures" className="mb-8 text-center text-2xl font-bold text-brand-900 sm:text-3xl">
          {t('home.captures.title')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🧾</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.captures.nfse.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.captures.nfse.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">📡</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.captures.nfcom.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.captures.nfcom.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">📄</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.captures.nfe.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.captures.nfe.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">📦</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.captures.others.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.captures.others.desc')}</p>
          </div>
        </div>
      </section>

      {/* Issue #594: Para quem — personas. */}
      <section aria-labelledby="home-personas">
        <h2 id="home-personas" className="mb-8 text-center text-2xl font-bold text-brand-900 sm:text-3xl">
          {t('home.personas.title')}
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🏢</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.personas.company.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.personas.company.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🧮</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.personas.accounting.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.personas.accounting.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🧑‍💻</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.personas.dev.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.personas.dev.desc')}</p>
          </div>
        </div>
      </section>

      {/* Diferenciais —Issue #594: dedup por chave de acesso/hash e trilha
          de auditoria entram na grade; LGPD cita servidor na UE + DPA
          (espelha a Politica de Privacidade). */}
      <section aria-labelledby="home-diff">
        <h2 id="home-diff" className="mb-8 text-center text-2xl font-bold text-brand-900 sm:text-3xl">
          {t('home.diff.title')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">⚡</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.features.extraction.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.features.extraction.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🧩</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.features.multilayout.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.features.multilayout.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🔁</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.diff.dedup.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.diff.dedup.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🔒</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.features.lgpd.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.features.lgpd.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">🧭</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.diff.audit.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.diff.audit.desc')}</p>
          </div>
          <div className="rounded-lg border border-brand-100 bg-white p-6 shadow-sm">
            <div className="mb-3 text-3xl" aria-hidden="true">📤</div>
            <h3 className="mb-2 font-semibold text-brand-800">{t('home.features.export.title')}</h3>
            <p className="text-sm text-brand-600">{t('home.features.export.desc')}</p>
          </div>
        </div>
      </section>

      {/* Issue #594: FAQ — <details>/<summary> nativos (acessiveis,
          funcionam sem JS extra). So promessas verificadas no backend. */}
      <section aria-labelledby="home-faq" className="mx-auto max-w-3xl">
        <h2 id="home-faq" className="mb-8 text-center text-2xl font-bold text-brand-900 sm:text-3xl">
          {t('home.faq.title')}
        </h2>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <details
              key={n}
              className="rounded-lg border border-brand-200 bg-white px-5 py-4 shadow-sm"
            >
              <summary className="cursor-pointer font-medium text-brand-800">
                {t(`home.faq.q${n}`)}
              </summary>
              <p className="mt-2 text-sm text-brand-600">{t(`home.faq.a${n}`)}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA final — Issue #594: leva a /pricing e /signup. */}
      <section className="rounded-lg border border-brand-200 bg-white p-8 text-center shadow-sm sm:p-12">
        <h2 className="mb-3 text-2xl font-bold text-brand-900 sm:text-3xl">
          {t('home.pricing.teaser.title')}
        </h2>
        <p className="mx-auto mb-6 max-w-xl text-brand-600">
          {t('home.pricing.teaser.desc')}
        </p>
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => onNavigate?.('signup')}
            className="inline-block rounded-md bg-brand-600 px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            {t('home.pricing.teaser.ctaSignup')}
          </button>
          <button
            type="button"
            onClick={() => onNavigate?.('pricing')}
            className="inline-block rounded-md border border-brand-300 bg-white px-6 py-3 font-semibold text-brand-700 transition-colors hover:bg-brand-50"
          >
            {t('home.pricing.teaser.cta')}
          </button>
        </div>
      </section>

      {/* Footer de links */}
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-brand-500" aria-label="Rodape">
        <button
          type="button"
          onClick={() => onNavigate?.('login')}
          className="hover:text-brand-700"
        >
          {t('home.footer.login')}
        </button>
        <span aria-hidden="true">&middot;</span>
        <button type="button" onClick={() => onNavigate?.('signup')} className="hover:text-brand-700">{t('home.footer.signup')}</button>
        <span aria-hidden="true">&middot;</span>
        <button type="button" onClick={() => onNavigate?.('pricing')} className="hover:text-brand-700">{t('home.footer.pricing')}</button>
        <span aria-hidden="true">&middot;</span>
        <a href="/dsar" className="hover:text-brand-700">{t('home.footer.dsar')}</a>
        <span aria-hidden="true">&middot;</span>
        <button
          type="button"
          onClick={() => onNavigate?.('terms')}
          className="hover:text-brand-700"
        >
          {t('home.footer.terms')}
        </button>
        <span aria-hidden="true">&middot;</span>
        <button
          type="button"
          onClick={() => onNavigate?.('privacy')}
          className="hover:text-brand-700"
        >
          {t('home.footer.privacy')}
        </button>
      </nav>
    </div>
  );
}

export default Home;
